import React, { useState } from "react";
import { COMPANY, SERVICES } from "../mock";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "../hooks/use-toast";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing details", description: "Please fill in your name, email and message." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast({ title: "Enquiry received", description: `Thanks ${form.name.split(" ")[0]}, our team will reach out within 24 hours.` });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    }, 900);
  };

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-700">Contact</div>
          <h2 className="display section-underline mt-3 text-4xl md:text-5xl font-extrabold text-slate-900 uppercase">
            Let’s talk about your project
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Share your requirements with our specialists. We respond within 24 hours with detailed inputs, BOQ guidance and timelines.
          </p>

          <ul className="mt-9 space-y-5">
            <li className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 rounded-lg bg-yellow-100 text-yellow-800 grid place-items-center"><Phone className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Call us</div>
                <div className="font-bold text-slate-900">{COMPANY.phone}</div>
                <div className="text-sm text-slate-600">{COMPANY.altPhone}</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 rounded-lg bg-yellow-100 text-yellow-800 grid place-items-center"><Mail className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Email</div>
                <div className="font-bold text-slate-900">{COMPANY.email}</div>
                <div className="text-sm text-slate-600">{COMPANY.salesEmail}</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 rounded-lg bg-yellow-100 text-yellow-800 grid place-items-center"><MapPin className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Head office</div>
                <div className="font-bold text-slate-900">{COMPANY.address}</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 rounded-lg bg-yellow-100 text-yellow-800 grid place-items-center"><Clock className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Working hours</div>
                <div className="font-bold text-slate-900">{COMPANY.hours}</div>
              </div>
            </li>
          </ul>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-slate-200">
          <h3 className="display text-2xl font-bold text-slate-900">Request a Quote</h3>
          <p className="text-sm text-slate-500 mt-1">All fields marked with * are required.</p>

          <div className="mt-7 grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Full Name *</label>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" className="mt-2 h-12" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email *</label>
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="name@company.com" className="mt-2 h-12" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Phone</label>
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91" className="mt-2 h-12" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Service Interested In</label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger className="mt-2 h-12"><SelectValue placeholder="Select a service" /></SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                  ))}
                  <SelectItem value="other">Other / Multiple</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Project Details *</label>
            <Textarea
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Tell us about your project scope, location, area and timelines..."
              className="mt-2 min-h-[140px]"
            />
          </div>

          <Button type="submit" disabled={loading} className="mt-7 h-12 px-7 bg-yellow-700 hover:bg-yellow-800 text-white font-semibold btn-shine">
            {loading ? "Sending..." : (<><Send className="w-4 h-4 mr-2" /> Send Enquiry</>)}
          </Button>
        </form>
      </div>
    </section>
  );
}
